'use client';

import dynamic from 'next/dynamic';
import {
  RichText,
  type RichTextClassConfig,
} from '@/app/_components/core/rich-text';
import { Separator } from '@/app/_components/core/separator';
import { SmartLink } from '@/app/_components/core/smart-link';
import { useLabel } from '@/app/_contexts/labels-context';
import { cn } from '@/app/_lib/utils';
import type { FooterComponentProps } from './footer.types';

// Footer additional content - loaded from @custom/_exports registry
const FooterAdditionalContentTop = dynamic(
  () =>
    import('@custom/_exports').then((mod) => ({
      default: mod.FooterAdditionalContentTop,
    })),
  { ssr: true }
);

const FooterAdditionalContentBottom = dynamic(
  () =>
    import('@custom/_exports').then((mod) => ({
      default: mod.FooterAdditionalContentBottom,
    })),
  { ssr: true }
);

// Shared text styles for footer links and copyright
const footerTextMuted = 'text-base font-normal text-muted-foreground';

// RichText class overrides for footer intro section
const footerRichText: RichTextClassConfig = {
  copytext: 'text-base font-normal text-muted-foreground',
  h3: 'text-base font-semibold text-foreground',
  h4: 'text-base font-semibold text-foreground',
};

export default function FooterDefault({
  copyright,
  copyrightText,
  footerMenu,
  legalMenu,
  theme,
  introText,
}: FooterComponentProps) {
  const ariaLabelFooter = useLabel('footerAriaLabel', 'Site footer');
  const ariaLabelNavigation = useLabel(
    'footerNavigationAriaLabel',
    'Footer navigation'
  );
  const ariaLabelLegalLinks = useLabel('legalLinksAriaLabel', 'Legal links');

  // Compute tokenized footer classes
  const getDefaultFooterClasses = () =>
    'bg-background text-sm pt-footer-padding-y-top-mobile pb-footer-padding-y-bottom-mobile xl:pt-footer-padding-y-top-desktop xl:pb-footer-padding-y-bottom-desktop';

  const getDesignTokenFooterClasses = () =>
    'rounded-tr-footer-top-right-radius rounded-tl-footer-top-left-radius';

  const getFooterClasses = () => {
    return cn(getDefaultFooterClasses(), getDesignTokenFooterClasses(), theme);
  };

  return (
    <footer
      id="footer"
      role="contentinfo"
      aria-label={ariaLabelFooter}
      className={getFooterClasses()}
    >
      <div className="container pb-8">
        {/* Footer Intro Section with Newsletter */}
        {/* No condition: the dynamic() slot wrapper is always truthy, so this
            never actually gated anything — the slot renders null when unused. */}
        {(
          <div className="mb-12 grid w-full grid-cols-1 gap-6 text-center lg:mb-16 lg:grid-cols-2 lg:gap-12 lg:text-left">
            {introText && (
              <RichText
                className="relative z-1 mx-auto lg:mx-0"
                classConfig={footerRichText}
              >
                {introText}
              </RichText>
            )}
            <FooterAdditionalContentTop />
          </div>
        )}

        {/* Main Navigation Grid */}
        {footerMenu.length > 0 && (
          <nav
            aria-label={ariaLabelNavigation}
            className="mb-12 w-full lg:mb-16"
          >
            <ul className="flex w-full flex-col items-start gap-12 lg:flex-row lg:flex-wrap lg:gap-6">
              {footerMenu.map((link, index) => (
                <li
                  key={link.id}
                  className="flex w-full flex-col gap-4 lg:min-w-45 lg:grow lg:basis-0"
                >
                  <SmartLink
                    href={link.slug}
                    target={link.linkTarget}
                    download={link.download}
                    className="w-full text-center text-base font-semibold text-foreground lg:text-left"
                    data-track-id={`footer-nav-${index + 1}`}
                  >
                    {link.title}
                  </SmartLink>
                  {link.children && link.children.length > 0 && (
                    <ul className="flex flex-col gap-3">
                      {link.children.map((child, childIndex) => (
                        <li
                          key={child.id}
                          className="w-full text-center lg:text-left"
                        >
                          <SmartLink
                            href={child.slug}
                            target={child.linkTarget}
                            download={child.download}
                            className={footerTextMuted}
                            data-track-id={`footer-nav-${index + 1}-sub-${childIndex + 1}`}
                          >
                            {child.title}
                          </SmartLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        )}

        {/* Section Divider */}
        <Separator role="presentation" className="w-full bg-muted-foreground" />

        {/* Bottom Section */}
        <nav className="mt-12 flex w-full flex-col-reverse items-center gap-12 lg:mt-16 lg:flex-row lg:justify-between lg:gap-6">
          {/* Copyright Text */}
          {(copyright || copyrightText) && (
            <p
              className={cn(
                footerTextMuted,
                'w-full text-center lg:w-auto lg:text-left lg:whitespace-pre'
              )}
              dangerouslySetInnerHTML={{
                __html: [copyright, copyrightText].filter(Boolean).join(' '),
              }}
            />
          )}

          {/* Legal Navigation */}
          {legalMenu.length > 0 && (
            <ul
              className="flex w-full flex-col items-center gap-6 text-center lg:w-auto lg:flex-row lg:gap-8 lg:text-left lg:whitespace-pre"
              aria-label={ariaLabelLegalLinks}
            >
              {legalMenu.map((link, index) => (
                <li key={link.id}>
                  <SmartLink
                    href={link.slug}
                    title={link.title}
                    target={link.linkTarget}
                    download={link.download}
                    className={cn(footerTextMuted, 'w-full lg:w-auto')}
                    data-track-id={`footer-legal-${index + 1}`}
                  />
                </li>
              ))}
            </ul>
          )}
        </nav>
      </div>

      {/* Footer Additional Content Bottom (e.g., Subfooter) */}
      <FooterAdditionalContentBottom />
    </footer>
  );
}
